import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../shell/bridge';
import './AgentSettings.css';

/**
 * Agent involvement — the two-path control from Jonathan's decision
 * (2026-10-02): "invoke only, users explicitly tag, otherwise a slider that
 * specifies level of involvement; agent persona to be defined later".
 *
 * PORTABLE BY DESIGN: this component owns its own stylesheet, takes a single
 * `visible` prop and touches only `/api/ai/agent-settings`, so when the full
 * Settings screen lands (Jonathan's next card) it moves there with one import
 * and one JSX tag — no edits to the AI screen beyond removing it here.
 *
 * It is rendered only while at least one AI permission toggle is on: no
 * permission, no behaviour control (toggles = what it may touch, this = how
 * much it speaks; both must hold for a card to ever appear).
 */

type Mode = 'invoke' | 'auto';

interface Settings {
  mode: Mode;
  involvement: number;
  persona: string | null;
  updatedAt: number;
  updatedBy: string | null;
}

const INVOLVEMENT_WORDS = ['Silent', 'Rarely', 'Sometimes', 'Often', 'Very often'] as const;
const INVOLVEMENT_MAX = INVOLVEMENT_WORDS.length - 1;

const OPTIONS: { key: Mode; title: string; note: string }[] = [
  {
    key: 'invoke',
    title: 'Invoke only',
    note: 'Quiet until one of you tags it. Nothing is guessed.',
  },
  {
    key: 'auto',
    title: 'Let it join in',
    note: 'It speaks on its own — you set how often below.',
  },
];

export function AgentSettings({ visible }: { visible: boolean }) {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Guards against an out-of-order response when the slider moves fast.
  const seq = useRef(0);
  // Latest slider value, kept in a ref because the save handlers fire on
  // pointerup/keyup/blur — by then a closure captured from an earlier render
  // can still hold the pre-drag number and silently persist the old value.
  const pendingInvolvement = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api<{ settings: Settings }>('/api/ai/agent-settings');
      setSettings(data.settings);
      setFailed(false);
    } catch (err) {
      console.error('[Pear] agent settings load failed', err);
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    if (visible) void load();
  }, [visible, load]);

  const save = useCallback(
    async (patch: Partial<Pick<Settings, 'mode' | 'involvement'>>) => {
      if (!settings) return;
      const id = ++seq.current;
      // Optimistic: the control must not lag behind the finger.
      const optimistic: Settings = { ...settings, ...patch };
      setSettings(optimistic);
      setSaving(true);
      setSaveError(null);
      try {
        const data = await api<{ settings: Settings }>('/api/ai/agent-settings', {
          method: 'PUT',
          body: patch as Record<string, unknown>,
        });
        if (id !== seq.current) return; // a newer change already landed
        setSettings(data.settings);
      } catch (err) {
        console.error('[Pear] agent settings save failed', err);
        if (id !== seq.current) return;
        setSettings(settings); // roll back to what the server said last
        setSaveError('Couldn’t save that. Try again.');
      } finally {
        if (id === seq.current) setSaving(false);
      }
    },
    [settings],
  );

  /** Commit the slider once the user lets go — always from the ref. */
  const flushInvolvement = () => {
    const next = pendingInvolvement.current;
    pendingInvolvement.current = null;
    // `pendingInvolvement` is the dirty flag — comparing against
    // `settings.involvement` would be wrong, because onChange has already
    // optimistically set that state, so the two are always equal and nothing
    // would ever be saved.
    if (next === null || !settings) return;
    void save({ involvement: next });
  };

  if (!visible) return null;

  return (
    <section className="agsec" aria-labelledby="agsec-title" aria-busy={saving}>
      <div className="agsec-head">
        <h3 className="agsec-title" id="agsec-title">
          How much should the concierge join in?
        </h3>
        <span className="agsec-status" aria-live="polite">
          {saveError ? saveError : saving ? 'Saving…' : ''}
        </span>
      </div>

      {settings === null && !failed && (
        <div className="agsec-loading">Loading…</div>
      )}

      {failed && (
        <p className="agsec-error">
          Couldn’t load your concierge settings. Switch a permission on above, or try
          again shortly.
        </p>
      )}

      {settings && (
        <>
          <fieldset className="agsec-modes">
            <legend className="agsec-legend">Choose one</legend>
            {OPTIONS.map(opt => {
              const checked = settings.mode === opt.key;
              return (
                <label
                  key={opt.key}
                  className={`agsec-option${checked ? ' is-on' : ''}`}
                >
                  <input
                    type="radio"
                    name="agent-mode"
                    value={opt.key}
                    checked={checked}
                    onChange={() => {
                      if (saving) return;
                      void save({ mode: opt.key });
                    }}
                  />
                  <span className="agsec-option-text">
                    <span className="agsec-option-title">{opt.title}</span>
                    <span className="agsec-option-note">{opt.note}</span>
                  </span>
                </label>
              );
            })}
          </fieldset>

          <div
            className={`agsec-level${settings.mode === 'auto' ? '' : ' is-off'}`}
          >
            <label className="agsec-level-label" htmlFor="agent-involvement">
              Level of involvement
              <span className="agsec-level-word">
                {INVOLVEMENT_WORDS[settings.involvement] ?? INVOLVEMENT_WORDS[0]}
              </span>
            </label>
            <input
              id="agent-involvement"
              className="agsec-range"
              type="range"
              min={0}
              max={INVOLVEMENT_MAX}
              step={1}
              value={settings.involvement}
              disabled={settings.mode !== 'auto'}
              aria-valuetext={
                INVOLVEMENT_WORDS[settings.involvement] ?? INVOLVEMENT_WORDS[0]
              }
              onChange={e => {
                const next = Number(e.target.value);
                if (Number.isNaN(next)) return;
                pendingInvolvement.current = next;
                setSettings({ ...settings, involvement: next });
              }}
              onPointerUp={() => void flushInvolvement()}
              onKeyUp={() => void flushInvolvement()}
              onBlur={() => void flushInvolvement()}
            />
            <div className="agsec-scale" aria-hidden="true">
              <span>Silent</span>
              <span>Very often</span>
            </div>
            {settings.mode !== 'auto' && (
              <p className="agsec-hint">
                Only used when “Let it join in” is selected.
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
}
